import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  BookOpen, Users, Building2, Download, Search,
  ChevronDown, AlertTriangle, CheckCircle, Info, X, ExternalLink
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (v) => {
  if (v === null || v === undefined) return '—';
  return 'R\u00A0' + Math.abs(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const ROW_COLOURS = {
  drawdown:   { row: 'bg-red-50/60',    debit: 'text-red-700 font-semibold',   credit: '' },
  interest:   { row: 'bg-amber-50/40',  debit: 'text-amber-700',               credit: '' },
  fee:        { row: 'bg-orange-50/40', debit: 'text-orange-700',               credit: '' },
  repayment:  { row: 'bg-green-50/60',  debit: '',                              credit: 'text-green-700 font-semibold' },
  investment: { row: 'bg-green-50/50',  debit: 'text-green-700 font-semibold', credit: '' },
  distribution:{ row: 'bg-blue-50/40',  debit: '',                              credit: 'text-blue-700' },
  withdrawal: { row: 'bg-purple-50/40', debit: '',                              credit: 'text-purple-700' },
  default:    { row: '',                debit: 'text-gray-800',                 credit: 'text-gray-800' },
};

const SOURCE_BADGE = {
  bank:        { cls: 'bg-blue-100 text-blue-700',    label: 'Bank' },
  cashflow:    { cls: 'bg-amber-100 text-amber-700',  label: 'Cashflow' },
  loan_book:   { cls: 'bg-gray-100 text-gray-600',    label: 'Loan Book' },
  interest_lb: { cls: 'bg-orange-100 text-orange-700', label: 'Interest' },
  interest_rp: { cls: 'bg-orange-100 text-orange-700', label: 'Interest' },
  rp_cashflow: { cls: 'bg-violet-100 text-violet-700', label: 'RP' },
  rp_book:     { cls: 'bg-violet-100 text-violet-700', label: 'RP' },
  txn:         { cls: 'bg-sky-100 text-sky-700',       label: 'TXN' },
};

function rowColour(entry) {
  const t = (entry.row_type || entry.txn_type || '').toLowerCase();
  if (t.includes('drawdown'))    return ROW_COLOURS.drawdown;
  if (t.includes('interest'))    return ROW_COLOURS.interest;
  if (t.includes('fee'))         return ROW_COLOURS.fee;
  if (t.includes('repayment'))   return ROW_COLOURS.repayment;
  if (t.includes('invest') || t.includes('capital_in') || t.includes('capital_injection')) return ROW_COLOURS.investment;
  if (t.includes('distribution') || t.includes('dist')) return ROW_COLOURS.distribution;
  if (t.includes('withdrawal') || t.includes('redemption')) return ROW_COLOURS.withdrawal;
  return ROW_COLOURS.default;
}

export default function AdminLedgerPage() {
  const { token } = useAuth();
  const headers = { Authorization: `Bearer ${token}` };
  const [searchParams] = useSearchParams();

  const [entityType, setEntityType] = useState(searchParams.get('entity') || 'investor');
  const [entities, setEntities] = useState({ investors: [], clients: [] });
  const [selectedId, setSelectedId] = useState(searchParams.get('id') || '');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entitySearch, setEntitySearch] = useState('');
  const [showEntityDropdown, setShowEntityDropdown] = useState(false);
  const dropdownRef = useRef(null);

  // Loan ledger modal state
  const [loanModal, setLoanModal] = useState(null); // { loanId, fromDate, toDate }
  const [loanLedger, setLoanLedger] = useState(null);
  const [loanLoading, setLoanLoading] = useState(false);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowEntityDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    const fetchEntities = async () => {
      setEntitiesLoading(true);
      try {
        const r = await axios.get(`${API_URL}/api/admin/ledger/entities`, { headers });
        setEntities(r.data);
      } catch { toast.error('Failed to load entities'); }
      finally { setEntitiesLoading(false); }
    };
    fetchEntities();
  }, [token]);

  // Auto-fetch ledger if entity was pre-selected via URL params
  useEffect(() => {
    if (selectedId && !ledger && !loading) {
      fetchLedger();
    }
  }, [selectedId, entities]);

  // Reset selection when switching entity type (only if not from URL)
  useEffect(() => {
    if (!searchParams.get('id')) {
      setSelectedId('');
      setLedger(null);
      setEntitySearch('');
    }
  }, [entityType]);

  const fetchLedger = useCallback(async () => {
    if (!selectedId) return;
    setLoading(true);
    try {
      const base = `${API_URL}/api/admin/ledger/${entityType}/${selectedId}`;
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const url = params.toString() ? `${base}?${params}` : base;
      const r = await axios.get(url, { headers });
      setLedger(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load ledger');
    } finally {
      setLoading(false);
    }
  }, [selectedId, entityType, fromDate, toDate, token]);

  const openLoanLedger = useCallback(async (loanId) => {
    setLoanModal({ loanId, fromDate, toDate });
    setLoanLedger(null);
    setLoanLoading(true);
    try {
      const params = new URLSearchParams();
      if (fromDate) params.set('from_date', fromDate);
      if (toDate) params.set('to_date', toDate);
      const url = `${API_URL}/api/admin/ledger/loan/${loanId}` + (params.toString() ? `?${params}` : '');
      const r = await axios.get(url, { headers });
      setLoanLedger(r.data);
    } catch (e) {
      toast.error('Failed to load loan ledger');
    } finally {
      setLoanLoading(false);
    }
  }, [fromDate, toDate, token]);

  const handleExport = () => {
    if (!selectedId) return;
    const base = `${API_URL}/api/admin/ledger/${entityType}/${selectedId}/export`;
    const params = new URLSearchParams();
    if (fromDate) params.set('from_date', fromDate);
    if (toDate) params.set('to_date', toDate);
    const url = params.toString() ? `${base}?${params}` : base;
    fetch(url, { headers })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `ledger_${selectedId}.csv`;
        a.click();
      }).catch(() => toast.error('Export failed'));
  };

  // Every investor's (or client's) ledger in one workbook — Summary sheet + one
  // sheet per entity. Uses the current Investor/Client toggle and date range.
  const [exportingAll, setExportingAll] = useState(false);
  const handleExportAll = async () => {
    setExportingAll(true);
    const params = new URLSearchParams({ entity_type: entityType });
    if (entityType === 'investor' && fromDate) params.set('from_date', fromDate);
    if (entityType === 'investor' && toDate) params.set('to_date', toDate);
    const toastId = toast.loading(`Building all ${entityType} ledgers — this can take a few minutes…`);
    try {
      const r = await fetch(`${API_URL}/api/admin/ledger/export-all.xlsx?${params}`, { headers });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `all_${entityType}_ledgers_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      toast.success('Export ready', { id: toastId });
    } catch (e) {
      toast.error(`Export failed (${e.message})`, { id: toastId });
    } finally {
      setExportingAll(false);
    }
  };

  const currentList = entityType === 'investor' ? entities.investors : entities.clients;
  const filteredList = currentList.filter(e =>
    !entitySearch || e.label.toLowerCase().includes(entitySearch.toLowerCase()) ||
    (e.email || '').toLowerCase().includes(entitySearch.toLowerCase()) ||
    (e.rp_code || '').toLowerCase().includes(entitySearch.toLowerCase()) ||
    e.id.toLowerCase().includes(entitySearch.toLowerCase())
  );

  const selectedEntity = currentList.find(e => e.id === selectedId);

  return (
    <div className="space-y-6" data-testid="admin-ledger-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-amber-600" />General Ledger
          </h1>
          <p className="text-gray-500 mt-1">Double-entry debit/credit view for all investors and clients</p>
        </div>
        <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={handleExport}
          disabled={!ledger}
          data-testid="export-csv-btn"
        >
          <Download className="h-4 w-4 mr-2" />Export CSV
        </Button>
        <Button
          variant="outline"
          onClick={handleExportAll}
          disabled={exportingAll}
          data-testid="export-all-xlsx-btn"
          title="One .xlsx with a Summary sheet and one sheet per entity (uses the Investor/Client toggle and date range below)"
        >
          <Download className="h-4 w-4 mr-2" />
          {exportingAll ? 'Exporting…' : `Export All ${entityType === 'investor' ? 'Investors' : 'Clients'} (.xlsx)`}
        </Button>
        </div>
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-5">
          <div className="flex flex-wrap gap-4 items-end">
            {/* Entity type toggle */}
            <div className="space-y-1">
              <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Account Type</Label>
              <div className="flex rounded-lg border overflow-hidden" data-testid="entity-type-toggle">
                <button
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${entityType === 'investor' ? 'bg-amber-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  onClick={() => setEntityType('investor')}
                  data-testid="toggle-investor"
                >
                  <Users className="h-4 w-4" />Investor
                </button>
                <button
                  className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium transition-colors ${entityType === 'client' ? 'bg-amber-500 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                  onClick={() => setEntityType('client')}
                  data-testid="toggle-client"
                >
                  <Building2 className="h-4 w-4" />Client
                </button>
              </div>
            </div>

            {/* Entity selector */}
            <div className="space-y-1 min-w-64 relative" ref={dropdownRef}>
              <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                {entityType === 'investor' ? 'Investor' : 'Client'}
              </Label>
              <div
                className="flex items-center border rounded-lg px-3 py-2 bg-white cursor-pointer hover:border-amber-400 transition-colors"
                onClick={() => setShowEntityDropdown(v => !v)}
                data-testid="entity-selector"
              >
                <Search className="h-4 w-4 text-gray-400 mr-2 shrink-0" />
                {selectedEntity
                  ? <span className="flex-1 text-sm font-medium text-gray-900">{selectedEntity.label}</span>
                  : <span className="flex-1 text-sm text-gray-400">Select {entityType}...</span>}
                <ChevronDown className="h-4 w-4 text-gray-400 ml-2 shrink-0" />
              </div>
              {showEntityDropdown && (
                <div className="absolute top-full left-0 right-0 z-50 bg-white border rounded-lg shadow-xl mt-1 max-h-64 overflow-hidden flex flex-col">
                  <div className="p-2 border-b">
                    <Input
                      autoFocus
                      placeholder="Search..."
                      value={entitySearch}
                      onChange={e => setEntitySearch(e.target.value)}
                      className="h-8 text-sm"
                      data-testid="entity-search-input"
                    />
                  </div>
                  <div className="overflow-y-auto flex-1">
                    {entitiesLoading ? (
                      <div className="p-3 text-sm text-gray-400 text-center">Loading...</div>
                    ) : filteredList.length === 0 ? (
                      <div className="p-3 text-sm text-gray-400 text-center">No results</div>
                    ) : filteredList.map(e => (
                      <div
                        key={e.id}
                        className={`px-3 py-2 cursor-pointer hover:bg-amber-50 transition-colors ${selectedId === e.id ? 'bg-amber-100' : ''}`}
                        onClick={() => { setSelectedId(e.id); setShowEntityDropdown(false); setEntitySearch(''); }}
                        data-testid={`entity-option-${e.id}`}
                      >
                        <p className="text-sm font-medium">{e.label}</p>
                        {e.email && <p className="text-xs text-gray-400">{e.email}{e.rp_code ? ` · RP: ${e.rp_code}` : ''}</p>}
                        {!e.email && e.id !== e.label && <p className="text-xs text-gray-400 font-mono">{e.id}</p>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Date range — investors only */}
            {entityType === 'investor' && (
              <>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">From</Label>
                  <Input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="h-9 w-36" data-testid="from-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-gray-500 uppercase tracking-wide">To</Label>
                  <Input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="h-9 w-36" data-testid="to-date" />
                </div>
              </>
            )}

            <Button
              onClick={fetchLedger}
              disabled={!selectedId || loading}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 h-9"
              data-testid="load-ledger-btn"
            >
              {loading ? 'Loading...' : 'Load Ledger'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards — Investor Ledger (cash truth, per spec) */}
      {ledger && entityType === 'investor' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-testid="ledger-summary">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Amount Invested</p>
              <p className="text-lg font-bold text-gray-900" data-testid="ledger-amount-invested">{fmt(ledger.amount_invested)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Sum of capital-in transactions</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Interest Earned</p>
              <p className="text-lg font-bold text-amber-700" data-testid="ledger-interest-earned">{fmt(ledger.interest_earned)}</p>
              <p className="text-xs text-gray-400 mt-0.5">RP interest + bank interest</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Deals Done</p>
              <p className="text-lg font-bold text-indigo-700" data-testid="ledger-total-deals">{fmt(ledger.total_deals_value)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Sum of positive cashflows</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Current Loan Exposure</p>
              <p className="text-lg font-bold text-blue-700" data-testid="ledger-current-exposure">{fmt(ledger.current_loan_exposure)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Closing balance · live loans</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Live Loans</p>
              <p className="text-lg font-bold text-slate-900" data-testid="ledger-live-loans">
                {ledger.live_loans_count || 0} <span className="text-sm text-slate-400">/ {ledger.total_loans_count || 0}</span>
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Active vs total</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Avg Interest Rate</p>
              <p className="text-lg font-bold text-emerald-700" data-testid="ledger-avg-rate">
                {(ledger.avg_interest_rate_pct || 0).toFixed(2)}%
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Weighted on live loans</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Summary Cards — Client/Loan view keeps the original layout */}
      {ledger && entityType !== 'investor' && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="ledger-summary">
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Debit</p>
              <p className="text-lg font-bold text-gray-900">{fmt(ledger.total_debit)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Drawdowns + charges</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Total Credit</p>
              <p className="text-lg font-bold text-gray-900">{fmt(ledger.total_credit)}</p>
              <p className="text-xs text-gray-400 mt-0.5">Repayments</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Closing Balance</p>
              <p className={`text-lg font-bold ${ledger.closing_balance >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                {fmt(ledger.closing_balance)}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">Outstanding balance</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Entries</p>
              <p className="text-lg font-bold text-gray-900">{ledger.entry_count}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {ledger.unique_loans ? `${ledger.unique_loans} unique loans` : 'transactions'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Discrepancy / Reconciliation Panel — hidden when status==ok (cash vs loans is not a discrepancy by design) */}
      {ledger && ledger.discrepancy_info && ledger.discrepancy_info.status && ledger.discrepancy_info.status !== 'ok' && (
        <Card data-testid="discrepancy-panel" className={
          ledger.discrepancy_info.status === 'error'   ? 'border-red-300 bg-red-50/50' :
          ledger.discrepancy_info.status === 'warning' ? 'border-amber-300 bg-amber-50/50' :
          'border-green-200 bg-green-50/50'
        }>
          <CardContent className="pt-4 pb-4">
            <div className="flex items-start gap-3">
              {ledger.discrepancy_info.status === 'error' && <AlertTriangle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />}
              {ledger.discrepancy_info.status === 'warning' && <AlertTriangle className="h-5 w-5 text-amber-500 mt-0.5 shrink-0" />}
              {ledger.discrepancy_info.status === 'ok' && <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 shrink-0" />}
              {ledger.discrepancy_info.status === 'unknown' && <Info className="h-5 w-5 text-gray-400 mt-0.5 shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold mb-2 ${
                  ledger.discrepancy_info.status === 'error'   ? 'text-red-700' :
                  ledger.discrepancy_info.status === 'warning' ? 'text-amber-700' :
                  ledger.discrepancy_info.status === 'unknown' ? 'text-gray-500' :
                  'text-green-700'
                }`}>
                  Reconciliation — {ledger.discrepancy_info.status === 'error' ? 'Issues Found' :
                                    ledger.discrepancy_info.status === 'warning' ? 'Review Recommended' :
                                    ledger.discrepancy_info.status === 'unknown' ? 'No Data Available' :
                                    'Balanced'}
                </p>
                {/* Alerts list with clickable actions */}
                <ul className="space-y-2">
                  {(ledger.discrepancy_info.alerts || []).map((alert, i) => {
                    // Handle both old string format and new object format
                    const isObject = typeof alert === 'object';
                    const message = isObject ? alert.message : alert;
                    const severity = isObject ? alert.severity : 'info';
                    const action = isObject ? alert.action : null;
                    const link = isObject ? alert.link : null;
                    
                    return (
                      <li key={i} className={`text-xs rounded-lg p-2 flex items-start gap-2 ${
                        severity === 'error' ? 'bg-red-100/50 border border-red-200' :
                        severity === 'warning' ? 'bg-amber-100/50 border border-amber-200' :
                        severity === 'ok' ? 'bg-green-100/50 border border-green-200' :
                        'bg-gray-100/50 border border-gray-200'
                      }`} data-testid={`alert-${i}`}>
                        <div className="flex-1">
                          <p className={`font-medium ${
                            severity === 'error' ? 'text-red-700' :
                            severity === 'warning' ? 'text-amber-700' :
                            severity === 'ok' ? 'text-green-700' :
                            'text-gray-700'
                          }`}>{message}</p>
                          {action && <p className="text-gray-500 mt-0.5">{action}</p>}
                        </div>
                        {link && (
                          <a
                            href={link}
                            className="shrink-0 flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium"
                            data-testid={`alert-link-${i}`}
                          >
                            Resolve <ExternalLink className="h-3 w-3" />
                          </a>
                        )}
                      </li>
                    );
                  })}
                </ul>
                {/* Investor-specific extra metrics */}
                {entityType === 'investor' && ledger.discrepancy_info.active_rp_balance > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-4 text-xs text-gray-600">
                    <span>Active RP Balance: <strong>{fmt(ledger.discrepancy_info.active_rp_balance)}</strong></span>
                    <span>Loans: <strong>{ledger.discrepancy_info.rp_loan_count}</strong></span>
                    {ledger.discrepancy_info.pending_staging_count > 0 &&
                      <span className="text-amber-700">Pending in staging: <strong>{ledger.discrepancy_info.pending_staging_count}</strong></span>}
                    {ledger.discrepancy_info.investec_actual_cash !== null && (
                      <span>Investec Cash: <strong>{fmt(ledger.discrepancy_info.investec_actual_cash)}</strong></span>
                    )}
                    {ledger.discrepancy_info.cash_discrepancy !== null && Math.abs(ledger.discrepancy_info.cash_discrepancy) > 100 && (
                      <span className={ledger.discrepancy_info.cash_discrepancy > 0 ? 'text-green-700' : 'text-red-700'}>
                        Cash Variance: <strong>{fmt(ledger.discrepancy_info.cash_discrepancy)}</strong>
                      </span>
                    )}
                  </div>
                )}
                {/* Client-specific extra metrics */}
                {entityType === 'client' && (
                  <div className="mt-3 pt-3 border-t border-gray-200 flex flex-wrap gap-4 text-xs text-gray-600">
                    <span>Active loans: <strong>{ledger.discrepancy_info.total_active_loans}</strong></span>
                    <span>Inactive loans: <strong>{ledger.discrepancy_info.total_inactive_loans}</strong></span>
                    {ledger.discrepancy_info.total_active_balance > 0 && (
                      <span>Active Balance: <strong>{fmt(ledger.discrepancy_info.total_active_balance)}</strong></span>
                    )}
                    {ledger.discrepancy_info.overdue_count > 0 &&
                      <span className="text-red-700">Overdue: <strong>{ledger.discrepancy_info.overdue_count}</strong></span>}
                    {ledger.discrepancy_info.closed_with_balance > 0 &&
                      <span className="text-red-700">Closed with balance: <strong>{ledger.discrepancy_info.closed_with_balance}</strong></span>}
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ledger Table */}
      {ledger && (
        <Card data-testid="ledger-table-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-3">
              <span>{ledger.entity_name}</span>
              {ledger.rp_code && <Badge variant="outline" className="font-mono text-xs">RP: {ledger.rp_code}</Badge>}
              {ledger.entity_email && <span className="text-sm font-normal text-gray-400">{ledger.entity_email}</span>}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="ledger-table">
                <thead>
                  <tr className="bg-gray-50 border-y">
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-28">Date</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Reference</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden xl:table-cell w-20">Source</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Debit</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Credit</th>
                    <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide w-36">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Opening Balance Row */}
                  <tr className="border-b bg-gray-50/80">
                    <td className="px-4 py-2.5 text-xs text-gray-400">—</td>
                    <td className="px-4 py-2.5 font-semibold text-gray-600 text-xs uppercase tracking-wide" colSpan={3}>Opening Balance</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">—</td>
                    <td className="px-4 py-2.5 text-right text-gray-500">—</td>
                    <td className="px-4 py-2.5 text-right font-semibold text-gray-700">{fmt(0)}</td>
                  </tr>

                  {ledger.entries.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-gray-400">
                        No transactions found{fromDate || toDate ? ' for the selected date range' : ''}
                      </td>
                    </tr>
                  ) : ledger.entries.map((entry, i) => {
                    const colours = rowColour(entry);
                    return (
                      <tr key={i} className={`border-b hover:bg-gray-50/80 transition-colors ${colours.row}`} data-testid={`ledger-row-${i}`}>
                        <td className="px-4 py-2.5 text-gray-500 font-mono text-xs">{entry.date || '—'}</td>
                        <td className="px-4 py-2.5 text-gray-800">{entry.description}</td>
                        <td className="px-4 py-2.5 text-gray-400 text-xs truncate max-w-xs hidden md:table-cell">
                          {entry.reference ? (
                            <button
                              onClick={() => openLoanLedger(entry.reference)}
                              className="hover:text-blue-600 hover:underline transition-colors cursor-pointer font-mono"
                              title={`View ledger for ${entry.reference}`}
                              data-testid={`loan-ledger-btn-${i}`}
                            >
                              {entry.reference}
                            </button>
                          ) : '—'}
                        </td>
                        <td className="px-4 py-2.5 hidden xl:table-cell">
                          {entry.source && SOURCE_BADGE[entry.source] && (
                            <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[entry.source].cls}`}>
                              {SOURCE_BADGE[entry.source].label}
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono whitespace-nowrap">
                          {entry.debit > 0 ? <span className="text-red-600 font-medium">{fmt(entry.debit)}</span> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className="px-4 py-2.5 text-right font-mono whitespace-nowrap">
                          {entry.credit > 0 ? <span className="text-green-600 font-medium">{fmt(entry.credit)}</span> : <span className="text-gray-300">—</span>}
                        </td>
                        <td className={`px-4 py-2.5 text-right font-mono font-medium whitespace-nowrap ${entry.balance >= 0 ? 'text-gray-800' : 'text-red-600'}`}>
                          {fmt(entry.balance)}
                        </td>
                      </tr>
                    );
                  })}

                  {/* Totals Row */}
                  {ledger.entries.length > 0 && (
                    <tr className="bg-gray-50 border-t-2 border-gray-300">
                      <td className="px-4 py-3" colSpan={4}>
                        <span className="text-xs font-bold uppercase tracking-wide text-gray-600">Closing Balance</span>
                      </td>
                      <td className="px-4 py-3 text-right font-bold font-mono text-red-600">{fmt(ledger.total_debit)}</td>
                      <td className="px-4 py-3 text-right font-bold font-mono text-green-600">{fmt(ledger.total_credit)}</td>
                      <td className={`px-4 py-3 text-right font-bold font-mono text-lg ${ledger.closing_balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                        {fmt(ledger.closing_balance)}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Empty state */}
      {!ledger && !loading && (
        <div className="text-center py-20 text-gray-400" data-testid="ledger-empty-state">
          <BookOpen className="h-12 w-12 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Select an {entityType} to view their ledger</p>
          <p className="text-sm mt-1">Use the controls above to choose an account</p>
        </div>
      )}

      {/* ── Loan Ledger Modal ─────────────────────────────────────────────── */}
      {loanModal && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-8 overflow-y-auto" data-testid="loan-ledger-modal">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl mx-4 mb-8">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50 rounded-t-xl">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Loan Ledger — {loanModal.loanId}</h2>
                {loanLedger && (
                  <p className="text-sm text-gray-500 mt-0.5">
                    {loanLedger.customer} · {loanLedger.interest_rate_display} · {loanLedger.purchase_date} → {loanLedger.repurchase_date || 'Active'}
                    {loanLedger.days_overdue > 0 && <span className="ml-2 text-red-600 font-medium">{loanLedger.days_overdue}d overdue</span>}
                  </p>
                )}
              </div>
              <button onClick={() => { setLoanModal(null); setLoanLedger(null); }}
                      className="p-2 rounded-lg hover:bg-gray-200 transition-colors" data-testid="close-loan-modal">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            {loanLoading ? (
              <div className="flex items-center justify-center py-20 text-gray-400">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3" />
                Loading loan ledger...
              </div>
            ) : loanLedger ? (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 px-6 py-4 border-b">
                  {[
                    { label: 'Loan Amount', value: fmt(loanLedger.loan_amount), cls: 'text-red-700' },
                    { label: 'Total Debits', value: fmt(loanLedger.total_debit), cls: 'text-red-700' },
                    { label: 'Total Credits', value: fmt(loanLedger.total_credit), cls: 'text-green-700' },
                    { label: 'Outstanding', value: fmt(loanLedger.closing_balance),
                      cls: loanLedger.closing_balance > 0 ? 'text-amber-700 font-bold' : 'text-green-700 font-bold' },
                  ].map((card, i) => (
                    <div key={i} className="bg-gray-50 rounded-lg p-3">
                      <p className="text-xs text-gray-500">{card.label}</p>
                      <p className={`text-base font-mono font-semibold ${card.cls}`}>{card.value}</p>
                    </div>
                  ))}
                </div>

                {/* Entries Table */}
                <div className="overflow-x-auto max-h-[55vh] overflow-y-auto">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 border-y z-10">
                      <tr>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-28">Date</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Description</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase hidden md:table-cell">Reference</th>
                        <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-20">Source</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-36">Debit</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-36">Credit</th>
                        <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase w-36">Balance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loanLedger.entries.map((entry, i) => {
                        const colours = rowColour(entry);
                        return (
                          <tr key={i} className={`border-b hover:bg-gray-50/80 ${colours.row}`}>
                            <td className="px-4 py-2 text-gray-500 font-mono text-xs">{entry.date || '—'}</td>
                            <td className="px-4 py-2 text-gray-800">{entry.description}</td>
                            <td className="px-4 py-2 text-gray-400 text-xs truncate max-w-xs hidden md:table-cell">{entry.reference}</td>
                            <td className="px-4 py-2">
                              {entry.source && SOURCE_BADGE[entry.source] && (
                                <span className={`inline-block px-1.5 py-0.5 rounded text-xs font-medium ${SOURCE_BADGE[entry.source].cls}`}>
                                  {SOURCE_BADGE[entry.source].label}
                                </span>
                              )}
                              {entry.source === 'computed' && (
                                <span className="inline-block px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Computed</span>
                              )}
                            </td>
                            <td className="px-4 py-2 text-right font-mono whitespace-nowrap">
                              {entry.debit > 0 ? <span className="text-red-600 font-medium">{fmt(entry.debit)}</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className="px-4 py-2 text-right font-mono whitespace-nowrap">
                              {entry.credit > 0 ? <span className="text-green-600 font-medium">{fmt(entry.credit)}</span> : <span className="text-gray-300">—</span>}
                            </td>
                            <td className={`px-4 py-2 text-right font-mono font-medium whitespace-nowrap ${entry.balance >= 0 ? 'text-gray-800' : 'text-red-600'}`}>
                              {fmt(entry.balance)}
                            </td>
                          </tr>
                        );
                      })}
                      {/* Totals */}
                      <tr className="bg-gray-50 border-t-2 border-gray-300">
                        <td colSpan={4} className="px-4 py-3 text-xs font-bold uppercase tracking-wide text-gray-600">Closing Balance</td>
                        <td className="px-4 py-3 text-right font-bold font-mono text-red-600">{fmt(loanLedger.total_debit)}</td>
                        <td className="px-4 py-3 text-right font-bold font-mono text-green-600">{fmt(loanLedger.total_credit)}</td>
                        <td className={`px-4 py-3 text-right font-bold font-mono text-lg ${loanLedger.closing_balance >= 0 ? 'text-green-700' : 'text-red-600'}`}>
                          {fmt(loanLedger.closing_balance)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                {loanLedger.entries.some(e => e.source === 'computed') && (
                  <p className="px-6 py-3 text-xs text-gray-400 border-t">
                    * Interest entries marked "Computed" are estimated daily compound interest for visualisation.
                    Re-import the Excel with CashflowValue1-29 columns for exact actuals.
                  </p>
                )}
              </>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
